// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import {PasswordResetStatus, UserService} from 'src/services/user-service';
import { NotificationService } from 'src/services/notification-service';
import { Router } from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import { CustomValidators } from 'src/app/shared/validators/custom-validators';
import {BehaviorSubject} from 'rxjs';
import {LOGIN_DEVICE_TOKEN} from '../login/login.component';
import { Partners } from '../../../types/partners';
import { ConfigurationService } from '../../../services/configuration-service';
import { UntilDestroy } from '@ngneat/until-destroy';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    standalone: false
})
export class ResetPasswordComponent implements OnInit {
  outerBanner: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  currentYear = new Date().getFullYear();
  form: UntypedFormGroup;
  showVerifyCode: Boolean;
  email = '';
  isEmailVerified: boolean;
  codeVerified: boolean;
  cigentPartner = Partners.Cigent;
  doqubizPartner = Partners.Doqubiz;
  digistorPartner = Partners.Digistor;
  kanguruPartner = Partners.Kanguru;
  lenovoPartner = Partners.Lenovo;
  vrsPartner = Partners.VRS;
  partnerId: Partners = Partners.Cigent;

  private formSubmitAttempt: boolean;

  fHeader: string;
  fSendCode: string;
  fIfMail: string;
  fEmail: string;
  fEmailAddress: string;
  fInvalidEmail: string;
  fResetCode: string;
  fVerify: string;
  fSend: string;
  fPassword: string;
  fWait: string;
  fErrorRequestingCode: string;
  fErrorResetPwdDeactivated: string;
  fErrorResetPwdNonCigent: string;
  submittingEmail = new BehaviorSubject<boolean>(false);

  showWait = false;
  seconds = 30;

  constructor(
    private fb: UntypedFormBuilder,
    private userService: UserService,
    private notification: NotificationService,
    public router: Router,
    private translate: TranslateService,
    private configuration: ConfigurationService,
  ) {
    this.translate.get(['RP_HEADER', 'RP_SEND_CODE', 'RP_IF_MAIL', 'RP_EMAIL', 'RP_EMAIL_ADD', 'RP_EMAIL_INVALID',
      'RP_RESET_CODE', 'RP_VERIFY', 'RP_SEND', 'RP_PASSWORD', 'RP_WAIT', 'ERROR_REQUESTING_CODE',
      'ERROR_RESET_PWD_DEACTIVATED', 'ERROR_RESET_NON_CIGENT']).subscribe(vals => {
      this.fHeader = vals['RP_HEADER'];
      this.fSendCode = vals['RP_SEND_CODE'];
      this.fIfMail = vals['RP_IF_MAIL'];
      this.fEmail = vals['RP_EMAIL'];
      this.fEmailAddress = vals['RP_EMAIL_ADD'];
      this.fInvalidEmail = vals['RP_EMAIL_INVALID'];
      this.fResetCode = vals['RP_RESET_CODE'];
      this.fVerify = vals['RP_VERIFY'];
      this.fSend = vals['RP_SEND'];
      this.fPassword = vals['RP_PASSWORD'];
      this.fWait = vals['RP_WAIT']
      this.fErrorRequestingCode = vals['ERROR_REQUESTING_CODE'];
      this.fErrorResetPwdDeactivated = vals['ERROR_RESET_PWD_DEACTIVATED'];
      this.fErrorResetPwdNonCigent = vals['ERROR_RESET_NON_CIGENT'];

    });
    this.showVerifyCode = false;
    this.isEmailVerified = false;
    this.codeVerified = false;
    this.partnerId = this.configuration.partnerId ?? Partners.Cigent;
  }

  ngOnInit() {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.pattern(CustomValidators.emailPattern),
        CustomValidators.maxByteLength(255)]],
      reset_code: ['', [Validators.minLength(12), Validators.maxLength(12)]],
    });

    const deviceToken = sessionStorage.getItem(LOGIN_DEVICE_TOKEN);
    if (!deviceToken) {
      this.outerBanner.next(true);
    }
  }

  isFieldInvalid(field: string) {
    return (!this.form.get(field).valid && this.form.get(field).touched) || (this.form.get(field).untouched && this.formSubmitAttempt);
  }

  onSubmit() {
    if (this.form.get('email').valid) {
      this.submittingEmail.next(true);
      this.userService.sendPasswordResetEmail(this.form.value.email, this.partnerId).subscribe({
        next: result => {
          switch (result.status) {
            case PasswordResetStatus.SUCCESS:
              this.showVerifyCode = true;
              break;
            case PasswordResetStatus.FAILURE:
            case PasswordResetStatus.INVALID:
              this.notification.showError(this.fErrorResetPwdDeactivated);
              break;
            case PasswordResetStatus.RESETS_THROTTLED:
              this.notification.showError(this.fErrorRequestingCode);
              this.showWait = true;
              this.count_down();
              break;
            default:
              this.notification.showError(this.translate.instant('ERROR_RESET_PWD_UNKNOWN'));
              break;
          }
        },
        error: result => {
          this.notification.showError(this.translate.instant('ERROR_RESET_PWD_UNKNOWN'));
        },
        complete: () => {
          this.submittingEmail.next(false);
        }
      });
    }
    this.formSubmitAttempt = true;
  }

  count_down() {
    this.seconds = 30;
    const countdownTimer = setInterval(() => {
      this.seconds -= 1;
      if ( this.seconds <= 0 ) {
        this.showWait = false;
        clearInterval(countdownTimer);
      }
    }, 1000);
  }

  onVerifyCode() {
    if (this.form.value.reset_code.length !== 12) {
      return;
    }
    this.userService.resetPasswordVerification(this.form.value.email, this.form.value.reset_code)
      .subscribe(() => {
        this.showVerifyCode = false;
        this.codeVerified = true;
        this.isEmailVerified = true;
      },
        () => {
        this.isEmailVerified = false;
        this.showVerifyCode = false;
        this.codeVerified = false;
        this.form.controls['reset_code'].setValue('');
        const title = this.translate.instant('INCORRECT_RESET_CODE_TITLE');
        const description = this.translate.instant('INCORRECT_RESET_CODE_DESC');
        this.notification.showError(description, title);
        this.showWait = true;
        this.count_down();
      }
    );
  }

}
